import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  HostListener,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SurveyDefinitionStepType, SurveySession, SurveySessionAnswer } from '../../data/survey.model';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { debounceTime, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-survey-custom',
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './survey-custom.component.html',
  styleUrl: './survey-custom.component.scss',
})
export class SurveyCustomComponent implements OnChanges, OnInit, OnDestroy {
  @Input() surveySession!: SurveySession;
  @Input() surveyTitle!: string;

  @Output() surveyFinished = new EventEmitter<SurveySessionAnswer['Answers']>();
  @Output() progressChange = new EventEmitter<number>();

  @ViewChild('answerContainerElement') answerContainerElement!: ElementRef;
  @ViewChild('answerOptionsElement') answerOptionsElement!: ElementRef;
  @ViewChildren('answerOptionElement') answerOptionElement!: QueryList<ElementRef>;

  stepTypes = SurveyDefinitionStepType;
  selectedAnswer: string | null = null;
  currentStepIndex = 1;
  message = new FormControl('');
  windowWidth = window.innerWidth;
  useColumnView = false;
  surveyAnswers: SurveySessionAnswer['Answers'] = {};

  private resizeSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  @HostListener('window:resize')
  onResize(): void {
    this.resizeSubject.next();
  }

  get descriptionAction(): string {
    if (this.currentStep && this.currentStep.type === SurveyDefinitionStepType.QuestionWithOptionSetSingleSelection) {
      return 'WebApp.v1.Survey.Description.ClickOption';
    }

    if (this.currentStep && this.currentStep.type === SurveyDefinitionStepType.QuestionWithText) {
      return 'WebApp.v1.Survey.Description.WriteYourAnswer';
    }

    return 'WebApp.v1.Shared.Buttons.Loading';
  }

  get description(): string | null {
    return this.steps[this.currentStepIndex]?.labels['Question'] || this.steps[0]?.labels['Content'];
  }

  get answerOptions(): SurveySession['steps'][number]['answerOptions'] {
    if (this.steps && this.steps.length > this.currentStepIndex) {
      return this.steps[this.currentStepIndex]?.answerOptions;
    }

    return [];
  }

  get currentStep(): SurveySession['steps'][number] | null {
    if (!this.steps) {
      return null;
    }

    return this.steps[this.currentStepIndex];
  }

  get steps(): SurveySession['steps'] {
    return this.surveySession?.steps;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['surveySession'] && changes['surveySession'].currentValue) {
      this.surveySession = changes['surveySession'].currentValue;
      this.updatePillProgress();
    }
  }

  ngOnInit(): void {
    this.resizeSubject
      .pipe(
        debounceTime(200),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.windowWidth = window.innerWidth;
        this.checkOverflow();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectAnswerOption(answer: string, value: string): void {
    this.selectedAnswer = answer;

    if (this.currentStep?.key) {
      this.surveyAnswers = { ...this.surveyAnswers, [this.currentStep.key]: value };
    }

    setTimeout(() => {
      this.selectedAnswer = null;
      this.currentStepIndex++;
      this.answerOptionElement.forEach(element => {
        element.nativeElement.blur();
      });
      this.updatePillProgress();

      if (this.currentStepIndex >= this.steps.length || this.steps[this.currentStepIndex].type === SurveyDefinitionStepType.ThankYou) {
        this.surveyFinished.emit(this.surveyAnswers);
      }
    }, 1000);

  }

  saveTextAnswer(): void {
    if (this.currentStep?.key && this.message.value) {
      this.surveyAnswers = { ...this.surveyAnswers, [this.currentStep.key]: this.message.value };
    }

    this.message.reset();
    this.currentStepIndex++;
    this.updatePillProgress();

    if (this.currentStepIndex >= this.steps.length || this.steps[this.currentStepIndex].type === SurveyDefinitionStepType.ThankYou) {
      this.surveyFinished.emit(this.surveyAnswers);
    }
  }

  skipStep(): void {
    if (this.currentStep?.key && !!this.surveyAnswers && !(this.currentStep.key in this.surveyAnswers)) {
      this.surveyAnswers = { ...this.surveyAnswers, [this.currentStep.key]: !this.message.value ? null : this.message.value };
    }

    this.currentStepIndex++;
    this.updatePillProgress();

    if (this.currentStepIndex >= this.steps.length || this.steps[this.currentStepIndex].type === SurveyDefinitionStepType.ThankYou) {
      this.surveyFinished.emit(this.surveyAnswers);
    }
  }

  updatePillProgress(): void {
    const stepsWithoutCoverPage = this.steps.filter(step => step.type !== SurveyDefinitionStepType.CoverPage);
    const progress = (this.currentStepIndex / stepsWithoutCoverPage.length) * 100;
    this.progressChange.emit(progress);
  }

  private checkOverflow(): void {
    if (this.answerOptionsElement && this.answerContainerElement) {
      const answerOptionsEl = this.answerOptionsElement.nativeElement;
      const answerContainerEl = this.answerContainerElement.nativeElement;

      const horizontalSpace = answerOptionsEl.clientWidth;
      const verticalSpace = answerContainerEl.clientHeight;
      const contentWidth = answerOptionsEl.scrollWidth;

      if (contentWidth > horizontalSpace && !this.useColumnView) {
        this.useColumnView = true;
      } else {
        const moreVerticalSpace = verticalSpace > horizontalSpace;
        this.useColumnView = moreVerticalSpace || this.windowWidth < 1100;
      }
    }
  }
}
